import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { getCountries } from "../../store/slices/companySlice";
import { getServices } from "../../store/slices/servicesSlice";
import { getCostServices } from "../../store/slices/costServicesSlice";
import {
  createContainerOrder,
  updateContainerOrder,
} from "../../store/slices/containerOrdersSlice";

import { ContainerOrderHeader } from "./ContainerOrderHeader";
import { ContainerOrderReadonly } from "./ContainerOrderReadonly";
import { GeneralInfoSection } from "./GeneralInfoSection";
import { ContainerDefaultsSection } from "./ContainerDefaultsSection";
import {
  RouteTerminalsSection,
  type StopBase,
  type StopAddressRefs,
} from "./RouteTerminalsSection";
import { OrderAttachmentsSection } from "./OrderAttachmentsSection";
// Sections 4–6 are shared verbatim with the shipment form.
import { PricingSection } from "../ShipmentForm/PricingSection";
import { ServicesSection } from "../ShipmentForm/ServicesSection";
import { CostServicesSection } from "../ShipmentForm/CostServicesSection";

import { CURRENCIES } from "../../constants/formOptions";
import { containerOrderSchema } from "../../schemavalidations/containerOrderSchema";
import { translateValidation } from "../../utils/translateValidation";
import type {
  ContainerOrder,
  ContainerOrderAttachment,
  ContainerOrderCopyData,
  ContainerOrderCopySource,
  ContainerOrderFormData,
} from "../../types/containerOrders";
import { useTranslation } from "react-i18next";

const GOOGLE_PLACES_API_KEY =
  import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || "";

interface ContainerOrderFormProps {
  order?: ContainerOrder;
  /**
   * An existing order's values, to start a new one from ("Copy order").
   *
   * Never set together with `order`: this is the create form filled in, not an
   * order being edited, and the save that follows creates a new record. The two
   * fields that name one booking (Reference, Booking number) and the source
   * order's documents arrive empty — see the server's copy endpoint.
   */
  copyData?: ContainerOrderCopyData;
  /** The order the copy came from, named at the top of the form. */
  copySource?: ContainerOrderCopySource;
  isEditMode: boolean;
  loading?: boolean;
  /**
   * Called after a save that reached beyond the order itself.
   *
   * Only the invoicing services do that — they are rewritten on the containers
   * already generated from the order — and the containers panel is rendered by
   * the page, not by this form, so it has to be told that what it is showing is
   * stale. Not called on an ordinary save: nothing below the form changed.
   */
  onSaved?: () => void;
}

/** The three route stops, in running order — pickup, loading, drop-off. */
const STOP_BASES: StopBase[] = [
  "route.terminalPickup",
  "route.loadingSite",
  "route.terminalDropoff",
];

const emptyAddress = {
  street: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  // Only ever set by a Google Places pick (or inherited from a directory
  // record that was picked); `undefined` is what the schema reads as "this
  // address has no map location yet".
  coordinates: undefined,
};

const emptyFormValues: ContainerOrderFormData = {
  referenceNumber: "",
  contractNumber: "",
  client: {
    partnerId: null,
    companyName: "",
    registerationCode: "",
    phoneNumber: "",
    emailAddress: "",
    address: { ...emptyAddress },
  },
  direction: "Outbound",
  incoterm: "",
  place: "",
  dateFrom: "",
  dateTo: "",
  containerType: "",
  shippingLine: "",
  vesselName: "",
  voyageNumber: "",
  blNumber: "",
  isoCode: "",
  defaultTareKg: null,
  defaultMaxPayloadKg: null,
  handling: {
    temperatureSensitive: false,
    temperatureRange: "",
    adr: false,
    imoClass: "",
    fragile: false,
    specifications: "",
  },
  route: {
    terminalPickup: {
      addressId: null,
      companyName: "",
      address: { ...emptyAddress },
      bookingRef: "",
      pin: "",
      pickupDate: "",
      pickupTime: "",
      // A new order collects and returns the box by default, which is what the
      // overwhelming majority of moves do. Turning either off is a decision the
      // dispatcher makes and has to give a reason for.
      enabled: true,
      skipReason: "",
    },
    loadingSite: {
      addressId: null,
      companyName: "",
      address: { ...emptyAddress },
      ramp: "",
      contactName: "",
      contactPhone: "",
      contactEmail: "",
      bookingRef: "",
      pin: "",
      onSiteDate: "",
      onSiteTime: "",
    },
    terminalDropoff: {
      addressId: null,
      companyName: "",
      address: { ...emptyAddress },
      bookingRef: "",
      pin: "",
      deliveryDate: "",
      deliveryTime: "",
      cutOffDate: "",
      cutOffTime: "",
      enabled: true,
      skipReason: "",
    },
    additionalStops: [],
  },
  pricing: {
    totalBuyingPrice: 0,
    totalSellingPrice: 0,
    pricePerKm: 0,
    mileage: 0,
    currency: "EUR",
  },
  services: [],
  costServices: [],
  additionalNotes: "",
};

/** An `_id`-bearing country ref comes back populated; the form wants the id. */
const countryId = (country: unknown): string => {
  if (!country) return "";
  if (typeof country === "string") return country;
  return (country as { _id?: string })._id ?? "";
};

/**
 * Map location of a stored address, or `undefined` when it has none.
 *
 * Records written before coordinates existed store `{ latitude: null,
 * longitude: null }`, which must read as "no location" — otherwise the form
 * would treat a legacy order as already located and let it save without a pick.
 */
const storedCoordinates = (
  coordinates?: { latitude?: number | null; longitude?: number | null } | null,
): { latitude: number; longitude: number } | undefined =>
  typeof coordinates?.latitude === "number" &&
  typeof coordinates?.longitude === "number"
    ? { latitude: coordinates.latitude, longitude: coordinates.longitude }
    : undefined;

/** Date fields round-trip as ISO strings; `<input type="date">` wants yyyy-mm-dd. */
const toDateInput = (value?: string | null): string =>
  value ? new Date(value).toISOString().slice(0, 10) : "";

/**
 * Also takes a copy's prefill, which is an order's worth of values without the
 * identity of one — every field it reads is optional either way.
 */
function orderToFormValues(
  order: ContainerOrder | ContainerOrderCopyData,
): ContainerOrderFormData {
  const route = order.route ?? {};

  return {
    referenceNumber: order.referenceNumber ?? "",
    contractNumber: order.contractNumber ?? "",
    client: {
      partnerId: order.client?.partnerId ?? null,
      companyName: order.client?.companyName ?? "",
      registerationCode: order.client?.registerationCode ?? "",
      phoneNumber: order.client?.phoneNumber ?? "",
      emailAddress: order.client?.emailAddress ?? "",
      address: {
        street: order.client?.address?.street ?? "",
        city: order.client?.address?.city ?? "",
        state: order.client?.address?.state ?? "",
        postalCode: order.client?.address?.postalCode ?? "",
        country: countryId(order.client?.address?.country),
        coordinates: storedCoordinates(order.client?.address?.coordinates),
      },
    },
    /**
     * Carried through the form untouched, though nothing renders it.
     *
     * The Direction input is gone but the field is not: `legOf` in
     * utils/containerCmr.ts reads it to decide which leg of the run carries the
     * goods on the consignment note. Dropping it from the payload would clear a
     * stored direction on the next save and quietly change what an order's CMR
     * prints — so an existing value round-trips, and only an order that somehow
     * has none falls back to the same default the model applies.
     *
     * `""` would be worse than either: the API's enum is Outbound | Inbound and
     * refuses an empty string, so a blank here would make every edit fail
     * validation on a field the user can no longer see.
     */
    direction: order.direction ?? "Outbound",
    incoterm: order.incoterm ?? "",
    place: order.place ?? "",
    dateFrom: toDateInput(order.dateFrom),
    dateTo: toDateInput(order.dateTo),
    containerType: order.containerType ?? "",
    shippingLine: order.shippingLine ?? "",
    vesselName: order.vesselName ?? "",
    voyageNumber: order.voyageNumber ?? "",
    blNumber: order.blNumber ?? "",
    isoCode: order.isoCode ?? "",
    defaultTareKg: order.defaultTareKg ?? null,
    defaultMaxPayloadKg: order.defaultMaxPayloadKg ?? null,
    handling: {
      temperatureSensitive: !!order.handling?.temperatureSensitive,
      temperatureRange: order.handling?.temperatureRange ?? "",
      adr: !!order.handling?.adr,
      imoClass: order.handling?.imoClass ?? "",
      fragile: !!order.handling?.fragile,
      specifications: order.handling?.specifications ?? "",
    },
    route: {
      terminalPickup: {
        addressId: route.terminalPickup?.addressId ?? null,
        companyName: route.terminalPickup?.companyName ?? "",
        address: {
          street: route.terminalPickup?.address?.street ?? "",
          city: route.terminalPickup?.address?.city ?? "",
          state: route.terminalPickup?.address?.state ?? "",
          postalCode: route.terminalPickup?.address?.postalCode ?? "",
          country: countryId(route.terminalPickup?.address?.country),
          coordinates: storedCoordinates(
            route.terminalPickup?.address?.coordinates,
          ),
        },
        bookingRef: route.terminalPickup?.bookingRef ?? "",
        pin: route.terminalPickup?.pin ?? "",
        pickupDate: toDateInput(route.terminalPickup?.pickupDate),
        pickupTime: route.terminalPickup?.pickupTime ?? "",
        // Absent on orders written before a stop could be turned off, and those
        // all collect the box — which is what `!== false` says.
        enabled: route.terminalPickup?.enabled !== false,
        skipReason: route.terminalPickup?.skipReason ?? "",
      },
      loadingSite: {
        addressId: route.loadingSite?.addressId ?? null,
        companyName: route.loadingSite?.companyName ?? "",
        address: {
          street: route.loadingSite?.address?.street ?? "",
          city: route.loadingSite?.address?.city ?? "",
          state: route.loadingSite?.address?.state ?? "",
          postalCode: route.loadingSite?.address?.postalCode ?? "",
          country: countryId(route.loadingSite?.address?.country),
          coordinates: storedCoordinates(
            route.loadingSite?.address?.coordinates,
          ),
        },
        ramp: route.loadingSite?.ramp ?? "",
        contactName: route.loadingSite?.contactName ?? "",
        contactPhone: route.loadingSite?.contactPhone ?? "",
        contactEmail: route.loadingSite?.contactEmail ?? "",
        bookingRef: route.loadingSite?.bookingRef ?? "",
        pin: route.loadingSite?.pin ?? "",
        onSiteDate: toDateInput(route.loadingSite?.onSiteDate),
        onSiteTime: route.loadingSite?.onSiteTime ?? "",
      },
      terminalDropoff: {
        addressId: route.terminalDropoff?.addressId ?? null,
        companyName: route.terminalDropoff?.companyName ?? "",
        address: {
          street: route.terminalDropoff?.address?.street ?? "",
          city: route.terminalDropoff?.address?.city ?? "",
          state: route.terminalDropoff?.address?.state ?? "",
          postalCode: route.terminalDropoff?.address?.postalCode ?? "",
          country: countryId(route.terminalDropoff?.address?.country),
          coordinates: storedCoordinates(
            route.terminalDropoff?.address?.coordinates,
          ),
        },
        bookingRef: route.terminalDropoff?.bookingRef ?? "",
        pin: route.terminalDropoff?.pin ?? "",
        deliveryDate: toDateInput(route.terminalDropoff?.deliveryDate),
        deliveryTime: route.terminalDropoff?.deliveryTime ?? "",
        cutOffDate: toDateInput(route.terminalDropoff?.cutOffDate),
        cutOffTime: route.terminalDropoff?.cutOffTime ?? "",
        enabled: route.terminalDropoff?.enabled !== false,
        skipReason: route.terminalDropoff?.skipReason ?? "",
      },
      /**
       * The stops this order adds between the three it always has.
       *
       * Every field is defaulted rather than spread, for the same reason the
       * three anchors are: react-hook-form registers inputs against these paths,
       * and an `undefined` where it expects a string turns a controlled input
       * into an uncontrolled one on the first keystroke.
       */
      additionalStops: (route.additionalStops ?? []).map((stop) => ({
        _id: stop._id,
        after: stop.after ?? "loadingSite",
        sequence: stop.sequence ?? 0,
        kind: stop.kind ?? "other",
        label: stop.label ?? "",
        comment: stop.comment ?? "",
        addressId: stop.addressId ?? null,
        partnerId: stop.partnerId ?? null,
        companyName: stop.companyName ?? "",
        address: {
          street: stop.address?.street ?? "",
          city: stop.address?.city ?? "",
          state: stop.address?.state ?? "",
          postalCode: stop.address?.postalCode ?? "",
          country: countryId(stop.address?.country),
          coordinates: storedCoordinates(stop.address?.coordinates),
        },
        ramp: stop.ramp ?? "",
        contactName: stop.contactName ?? "",
        contactPhone: stop.contactPhone ?? "",
        contactEmail: stop.contactEmail ?? "",
        bookingRef: stop.bookingRef ?? "",
        pin: stop.pin ?? "",
        date: toDateInput(stop.date),
        time: stop.time ?? "",
        enabled: stop.enabled !== false,
        skipReason: stop.skipReason ?? "",
      })),
    },
    pricing: {
      totalBuyingPrice: order.pricing?.totalBuyingPrice ?? 0,
      totalSellingPrice: order.pricing?.totalSellingPrice ?? 0,
      pricePerKm: order.pricing?.pricePerKm ?? 0,
      mileage: order.pricing?.mileage ?? 0,
      currency: order.pricing?.currency ?? "EUR",
    },
    services: order.services ?? [],
    costServices: order.costServices ?? [],
    additionalNotes: order.additionalNotes ?? "",
  };
}

export function ContainerOrderForm({
  order,
  copyData,
  copySource,
  isEditMode,
  loading = false,
  onSaved,
}: ContainerOrderFormProps) {
  const { t } = useTranslation(["containerOrders", "common"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { countries } = useAppSelector((state) => state.company);

  /**
   * The charges this order may no longer change, and the containers that say so.
   *
   * The order's invoicing services are the batch's price list: editing one
   * rewrites it on every container generated from the order. Once any of those
   * containers has been invoiced for a charge, that charge is a record — so the
   * row is drawn locked here rather than left editable for a save that is going
   * to refuse it. Learning at Save time that a price is frozen means the user
   * has already retyped it, and everything they changed around it is sitting in
   * a form the server has just rejected.
   *
   * Read from the store rather than taken as a prop because it is loaded with
   * the order and refreshed by the save, both of which the slice already owns.
   */
  const serviceLocks = useAppSelector(
    (state) => state.containerOrders.currentServiceLocks,
  );

  const lockedServiceRows = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [rowId, containers] of Object.entries(serviceLocks ?? {})) {
      out[rowId] = t("hints.serviceLockedOnContainers", {
        containers: containers.join(", "),
      });
    }
    return out;
  }, [serviceLocks, t]);
  const { services } = useAppSelector((state) => state.services);
  const { costServices } = useAppSelector((state) => state.costServices);
  const { saving } = useAppSelector((state) => state.containerOrders);

  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const [newCustomsAttachments, setNewCustomsAttachments] = useState<File[]>([]);
  const [storedAttachments, setStoredAttachments] = useState<
    ContainerOrderAttachment[]
  >(order?.attachments ?? []);

  /**
   * The optimistic-concurrency token, held in state rather than read off the
   * `order` prop at save time.
   *
   * Because this form can write to the order without saving it: deleting an
   * attachment removes it there and then, which moves the order's `updatedAt`
   * while the prop keeps the value it was loaded with. Saving afterwards was
   * refused as a lost update — and reported as somebody else having changed the
   * order, when the somebody was this user, one click earlier. The delete hands
   * the fresh token back and it is kept here.
   */
  const [versionToken, setVersionToken] = useState<string | undefined>(
    order?.updatedAt,
  );

  /**
   * Viewing and editing are two modes, not one form with disabled inputs.
   *
   * Opening a saved order lands in read mode: most visits are to *check*
   * something, and a page full of live inputs invites accidental edits to a
   * record other people's shipments were generated from. "Edit" opens the form;
   * saving returns to read mode, which also doubles as confirmation that the
   * save landed.
   *
   * A cancelled order is a closed record — it is pinned to read mode until it
   * is reopened.
   */
  const isCancelled = order?.status === "cancelled";
  const [viewMode, setViewMode] = useState<"read" | "edit">(
    isEditMode ? "read" : "edit",
  );

  const readonly = isCancelled || viewMode === "read";

  // A cancellation can arrive while the form is open — from this page's own
  // header, or from another tab. Drop straight back to read mode rather than
  // leaving editable inputs over a closed record.
  useEffect(() => {
    if (isCancelled) setViewMode("read");
  }, [isCancelled]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitted },
  } = useForm<ContainerOrderFormData>({
    // A copy fills the same form a blank order would get: from here on it *is*
    // a new order, and every value in it is the user's to change before saving.
    defaultValues: order
      ? orderToFormValues(order)
      : copyData
        ? orderToFormValues(copyData)
        : emptyFormValues,
    resolver: yupResolver(containerOrderSchema) as any,
    mode: "onChange",
  });

  const {
    fields: serviceFields,
    append: appendService,
    remove: removeService,
    replace: replaceServices,
  } = useFieldArray({ control, name: "services" });

  const {
    fields: costServiceFields,
    append: appendCostService,
    remove: removeCostService,
  } = useFieldArray({ control, name: "costServices" });

  useEffect(() => {
    dispatch(getCountries());
    dispatch(getServices({ search: "", page: 1, limit: 1000 }));
    dispatch(getCostServices({ search: "", page: 1, limit: 1000 }));
  }, [dispatch]);

  /**
   * The view mode, readable from an effect without making it a dependency.
   *
   * The re-seed below has to know whether the user is mid-edit, but it must not
   * *run* because the mode changed: toggling read/edit is not new data, and
   * re-seeding on the way into read mode would throw away edits the user is
   * about to toggle back to.
   */
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;

  /**
   * Re-sync the form with the order, except for what the user is still typing.
   *
   * This fires whenever the store hands back a new order object — which is not
   * only a fresh load. The containers panel sits on this same page, and adding,
   * cancelling, restoring or deleting a container (and generating a batch) all
   * re-read the order afterwards, as does cancelling or reopening it. Every one
   * of those produced a new object here, and re-seeding the inputs from it
   * silently discarded whatever was in them — most damagingly the charge table,
   * where the next Save then wrote the *old* price list back over the new one
   * and reported success.
   *
   * So the inputs are only re-seeded while the form is in read mode, where
   * there is nothing to lose and the page must show the stored truth. In edit
   * mode the inputs are left alone and only the bookkeeping is refreshed:
   *
   *   · `versionToken` — the optimistic-concurrency token. It has to track the
   *     server or the next Save is refused as a lost update. Advancing it while
   *     editing is safe *because every writer of this order is on this page*:
   *     the order is re-read only by actions this same user just performed.
   *     Should the order ever start being refreshed from outside (a socket, a
   *     poll), this is the line that has to change — advancing the token for
   *     somebody else's write would let this form overwrite it unseen.
   *
   *   · `storedAttachments` — the uploaded-document list. It is rendered, not
   *     edited: the form only ever adds pending files alongside it, so taking
   *     the server's copy cannot lose anything the user has typed.
   *
   * A save does its own re-seed from the response (see `onSubmit`), because
   * that is the one case where the inputs *must* be refreshed while they are
   * still in edit mode — new charge rows come back with the ids the next save
   * has to quote.
   */
  useEffect(() => {
    if (!order) return;

    setStoredAttachments(order.attachments ?? []);
    // A freshly loaded (or freshly saved) order is the new baseline.
    setVersionToken(order.updatedAt);

    if (viewModeRef.current === "edit") return;

    reset(orderToFormValues(order));
  }, [order, reset]);

  /**
   * Handles on the three stop address inputs.
   *
   * A missing map location is a validation error on `address.coordinates`,
   * which has no input of its own — so react-hook-form's own focus-first-error
   * cannot land on anything. Without this, saving an order whose stops were
   * typed by hand would appear to do nothing when the route section is scrolled
   * off screen.
   */
  const pickupAddressRef = useRef<HTMLInputElement | null>(null);
  const loadingAddressRef = useRef<HTMLInputElement | null>(null);
  const dropoffAddressRef = useRef<HTMLInputElement | null>(null);
  const stopAddressRefs: StopAddressRefs = {
    "route.terminalPickup": pickupAddressRef,
    "route.loadingSite": loadingAddressRef,
    "route.terminalDropoff": dropoffAddressRef,
  };

  /** Dotted paths of every leaf error, e.g. `route.loadingSite.address.city`. */
  const errorPaths = (node: any, prefix = ""): string[] => {
    if (!node || typeof node !== "object") return [];
    if (typeof node.message === "string") return [prefix];
    return Object.entries(node).flatMap(([key, value]) =>
      errorPaths(value, prefix ? `${prefix}.${key}` : key),
    );
  };

  const focusFirstUnlocatedStop = (formErrors: typeof errors) => {
    const paths = errorPaths(formErrors);
    // Something else is wrong too — react-hook-form has already focused that
    // field, and stealing focus from it would be worse than leaving it be.
    if (!paths.every((path) => /^route\.\w+\.address\./.test(path))) return;

    // `startsWith`, not equality: a half-filled coordinate pair reports on the
    // child key (`…coordinates.longitude`) rather than the object.
    const missing = STOP_BASES.find((base) =>
      paths.some((path) => path.startsWith(`${base}.address.coordinates`)),
    );
    if (!missing) return;
    const input = stopAddressRefs[missing].current;
    input?.focus();
    input?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  const servicesData = useWatch({ control, name: "services" }) || [];
  const costServicesData = useWatch({ control, name: "costServices" }) || [];

  const { totalForEachRow, grandTotal } = useMemo(() => {
    const rows = servicesData.map((item) => ({
      ...item,
      total: (item.qty || 0) * (item.price || 0),
    }));
    return {
      totalForEachRow: rows,
      grandTotal: rows.reduce((sum, item) => sum + item.total, 0),
    };
  }, [servicesData]);

  const { totalForEachRow: costRows, grandTotal: costGrandTotal } =
    useMemo(() => {
      const rows = costServicesData.map((item) => ({
        ...item,
        total: (item.qty || 0) * (item.price || 0),
      }));
      return {
        totalForEachRow: rows,
        grandTotal: rows.reduce((sum, item) => sum + item.total, 0),
      };
    }, [costServicesData]);

  const getErrorMessage = (error: any): string | undefined =>
    typeof error?.message === "string"
      ? translateValidation(error.message)
      : undefined;

  const onSubmit = async (data: ContainerOrderFormData) => {
    // Service rows carry a `total` derived from price × qty; the API validates
    // that it matches, so recompute rather than trusting whatever is in state.
    const withTotals = (rows?: ContainerOrderFormData["services"]) =>
      (rows ?? []).map((row) => ({
        ...row,
        total: (row.qty || 0) * (row.price || 0),
      }));

    // `valueAsNumber` turns an empty numeric input into NaN, which would be
    // serialised as the string "NaN" and rejected by the API. Clearing a number
    // field means "no value", so send null.
    const num = (value: unknown): number | null =>
      typeof value === "number" && Number.isFinite(value) ? value : null;

    // A cleared `<input type="date">` reads as "", which the API cannot cast to
    // a date. Null is the value that says "there is no date here" — and it is
    // what actually unsets a date that was previously stored.
    const date = (value?: string | null): string | null => value || null;

    const payload: ContainerOrderFormData = {
      ...data,
      dateFrom: date(data.dateFrom),
      dateTo: date(data.dateTo),
      route: {
        ...data.route,
        // Every stop date gets the same treatment as the cut-off: a cleared
        // date input reads as "", which the API cannot cast to a date, and only
        // an explicit null unsets one the order already had.
        terminalPickup: {
          ...data.route.terminalPickup,
          pickupDate: date(data.route?.terminalPickup?.pickupDate),
        },
        loadingSite: {
          ...data.route.loadingSite,
          onSiteDate: date(data.route?.loadingSite?.onSiteDate),
        },
        terminalDropoff: {
          ...data.route.terminalDropoff,
          deliveryDate: date(data.route?.terminalDropoff?.deliveryDate),
          cutOffDate: date(data.route?.terminalDropoff?.cutOffDate),
        },
      },
      defaultTareKg: num(data.defaultTareKg),
      defaultMaxPayloadKg: num(data.defaultMaxPayloadKg),
      pricing: {
        ...data.pricing,
        totalBuyingPrice: num(data.pricing?.totalBuyingPrice) ?? 0,
        totalSellingPrice: num(data.pricing?.totalSellingPrice) ?? 0,
        pricePerKm: num(data.pricing?.pricePerKm) ?? 0,
        mileage: num(data.pricing?.mileage) ?? 0,
      },
      services: withTotals(data.services),
      costServices: withTotals(data.costServices),
      attachments: newAttachments,
      customsAttachments: newCustomsAttachments,
    };

    try {
      if (isEditMode && order) {
        const { containersUpdated, order: savedOrder } = await dispatch(
          updateContainerOrder({
            id: order._id,
            // Optimistic-concurrency token: as loaded, or as re-issued by an
            // attachment delete this form performed since.
            data: { ...payload, updatedAt: versionToken },
          }),
        ).unwrap();

        /**
         * Re-seed from the response, here rather than leaving it to the effect
         * above — which deliberately will not touch inputs that are still in
         * edit mode, and cannot tell this save apart from a background re-read.
         *
         * It is not cosmetic. A charge the user just added was sent without an
         * `_id` and comes back with the one Mongoose minted for it, and that id
         * is what threads the row to the copies now sitting on every container.
         * Leaving the form holding the id-less version means the next save
         * reads as "delete that charge, add an identical one" — which rewrites
         * the whole batch's price list for a save that changed nothing, and is
         * refused outright once any container has been invoiced for it.
         */
        reset(orderToFormValues(savedOrder));

        /**
         * Say when the save left the order.
         *
         * Every other field on this form is a default and stops at the order;
         * the invoicing services do not — they are the batch's price list and
         * are rewritten on the containers already generated. Rewriting thirty
         * containers' charges behind a bare "Order updated" is a surprise, and
         * the containers panel below is at that moment showing figures the
         * server has just replaced, so it is refetched too.
         */
        if (containersUpdated) {
          toast.success(
            t("toasts.servicesApplied", { count: containersUpdated }),
          );
          onSaved?.();
        } else {
          toast.success(t("toasts.updated"));
        }
        setNewAttachments([]);
        setNewCustomsAttachments([]);
        // Back to read mode: it confirms the save landed and returns the page
        // to the state most visits want it in.
        setViewMode("read");
      } else {
        const created = await dispatch(
          createContainerOrder(payload),
        ).unwrap();
        toast.success(t("toasts.created"));
        navigate(`/container-orders/${created._id}`);
      }
    } catch (error: any) {
      const message =
        typeof error === "string" ? error : (error?.message ?? null);

      /**
       * The one refusal the form has to *act* on, not just report.
       *
       * The server refused because a charge the save was changing has already
       * been invoiced on a container. The offending values are still in the
       * inputs, so every further press of Save is refused identically and the
       * only way out is a page reload — which throws away every other edit on
       * the form. Putting just the charge table back to what is stored leaves
       * the user exactly where they can carry on: the rest of their work is
       * intact and the charges are the truth again.
       *
       * Only reachable in a race now that the rows are drawn locked — somebody
       * invoiced a container between this form loading and this save. Worth
       * keeping for precisely that reason: it is the case the lock cannot see.
       */
      if (error?.code === "SERVICES_INVOICED" && order) {
        replaceServices(order.services ?? []);
      }

      toast.error(message || t("toasts.saveFailed"));
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, focusFirstUnlocatedStop)}
      className="pb-10"
    >
      <ContainerOrderHeader
        order={order}
        saving={saving}
        loading={loading}
        readonly={readonly}
        isCancelled={isCancelled}
        viewMode={viewMode}
        canToggleMode={isEditMode && !isCancelled}
        onToggleMode={() =>
          setViewMode((mode) => (mode === "read" ? "edit" : "read"))
        }
        onBack={() => navigate("/container-orders")}
      />

      {/* Capped rather than full-bleed: these are inline label/field rows, and
          past roughly this width the controls just stretch — a 700px-wide
          "Reference" box is harder to scan, not easier. */}
      <div className="mx-auto w-full max-w-360 space-y-3 px-3 sm:px-4">
        {/* What the copy did and did not bring with it.
            Said on the way in, not discovered on the way out: the form is full
            of another booking's values, and the two boxes that identify a
            booking are the two that are empty. */}
        {copyData && (
          <p className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-[12px] text-primary-800">
            {copySource?.orderNumber
              ? t("hints.copiedFrom", {
                  orderNumber: copySource.orderNumber,
                })
              : t("hints.copied")}
          </p>
        )}

        {/* Editing an order that already has children is allowed, but it is
            not retroactive — say so once, here, rather than letting the user
            discover it by comparing an order against its shipments. */}
        {isEditMode && !!order?.lineProgress?.total && !readonly && (
          <p className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-[12px] text-primary-800">
            {t("hints.editAfterGenerate")}
          </p>
        )}
      </div>

      {/* Read mode is a different presentation, not the same form greyed out —
          see ContainerOrderReadonly. */}
      {isEditMode && readonly && order ? (
        <div className="mx-auto w-full max-w-360 px-3 sm:px-4">
          <ContainerOrderReadonly order={order} />
        </div>
      ) : (
      <div className="mx-auto w-full max-w-360 space-y-3 px-3 sm:px-4">
        {/* 1 + 2 — general information and container defaults, side by side. */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <GeneralInfoSection
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            readonly={readonly}
            getErrorMessage={getErrorMessage}
          />

          <ContainerDefaultsSection
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            readonly={readonly}
            getErrorMessage={getErrorMessage}
          />
        </div>

        {/* 3 — route & terminals */}
        <RouteTerminalsSection
          control={control}
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          readonly={readonly}
          getErrorMessage={getErrorMessage}
          countries={countries?.flat() ?? []}
          apiKey={GOOGLE_PLACES_API_KEY}
          isSubmitted={isSubmitted}
          addressRefs={stopAddressRefs}
        />

        {/* 4 — pricing (shared with the shipment form) */}
        <PricingSection<ContainerOrderFormData>
          register={register}
          errors={errors}
          readonly={readonly}
          getErrorMessage={getErrorMessage}
          currencies={CURRENCIES}
          transportMode="CONTAINER"
        />

        {/* 5 — invoicing services (shared with the shipment form, which calls
            the same table "Additional Services" — see the `title` prop).

            The one block on this form that is not a default. Said here, next to
            the table it is true of, rather than only in the banner at the top:
            a user editing a price is looking at the price, and the banner three
            sections up says the opposite about everything else. */}
        {isEditMode && !!order?.lineProgress?.total && !readonly && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
            {t("hints.servicesRetroactive")}
          </p>
        )}
        <ServicesSection<ContainerOrderFormData>
          title={t("sections.invoicingServices")}
          register={register}
          errors={errors}
          readonly={readonly}
          serviceFields={serviceFields}
          appendService={appendService}
          removeService={removeService}
          totalForEachRow={totalForEachRow}
          grandTotal={grandTotal}
          services={services}
          setValue={setValue}
          control={control}
          // Order rows are templates — invoicing happens on the child shipments.
          hideInvoicedColumn
          // No driver writes a service onto a container — see `hideByColumn`.
          hideByColumn
          // Frozen by the batch rather than by a row's own invoice — the order
          // itself is never invoiced, so nothing here carries an `invoiceId`.
          lockedRows={lockedServiceRows}
        />

        {/* 6 — cost services (shared with the shipment form) */}
        <CostServicesSection<ContainerOrderFormData>
          register={register}
          errors={errors}
          readonly={readonly}
          costServiceFields={costServiceFields}
          appendCostService={appendCostService}
          removeCostService={removeCostService}
          totalForEachRow={costRows}
          grandTotal={costGrandTotal}
          services={costServices}
          setValue={setValue}
          control={control}
        />

        {/* 7 — attachments & documents */}
        <OrderAttachmentsSection
          register={register}
          setValue={setValue}
          readonly={readonly}
          newAttachments={newAttachments}
          setNewAttachments={setNewAttachments}
          newCustomsAttachments={newCustomsAttachments}
          setNewCustomsAttachments={setNewCustomsAttachments}
          orderId={order?._id}
          storedAttachments={storedAttachments}
          onAttachmentDeleted={(id, updatedAt) => {
            setStoredAttachments((prev) => prev.filter((a) => a._id !== id));
            // The delete wrote to the order, so the token it returns is the
            // one this form must save against.
            if (updatedAt) setVersionToken(updatedAt);
          }}
        />
      </div>
      )}
    </form>
  );
}
